import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ChevronDown, Calendar, Search, Loader2 } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { api } from '../../services/api';
import { STOCK_LIST, loadStockList, searchStocks } from '../../data/stockList';
import { useProfile } from '../../contexts/ProfileContext';
import { loadOhlcByPath } from '../../services/duckdbService';
import { getDoneSummaryBlobPath, parseDoneSummaryCSV, fetchDoneSummaryCsv } from '../../utils/doneSummaryBlob';
import { getOhlcBlobPath } from '../../utils/sectorMappingCache';

interface PriceData {
  price: number;
  bFreq: number;
  bLot: number;
  bOrd: number;
  sLot: number;
  sFreq: number;
  sOrd: number;
  tFreq: number;
  tLot: number;
  tOrd: number;
}




// Generate realistic price data based on BBRI.csv structure (DEPRECATED - using real data now)
/*
const generatePriceData = (stock: string, date: string): PriceData[] => {
  const basePrice = stock === 'BBRI' ? 4150 : stock === 'BBCA' ? 2750 : stock === 'BMRI' ? 3200 : 1500;
  
  // Create a seed based on stock and date for consistent data
  const seed = stock.charCodeAt(0) + date.split('-').reduce((acc, part) => acc + parseInt(part), 0);
  
  const data: PriceData[] = [];
  
  // Generate 15-20 price levels around base price
  for (let i = -10; i <= 10; i++) {
    const price = basePrice + (i * 10);
    const priceSeed = seed + i * 100;
    
    // Skip some prices based on seed (consistent skipping)
    if ((priceSeed % 10) > 2) { // 70% chance of having data
      const bLot = (priceSeed * 123) % 50000000;
      const sLot = (priceSeed * 456) % 50000000;
      const bFreq = (priceSeed * 789) % 5000;
      const sFreq = (priceSeed * 321) % 5000;
      
      data.push({
        price,
        bFreq,
        bLot,
        sLot,
        sFreq,
        tFreq: bFreq + sFreq,
        tLot: bLot + sLot
      });
    }
  }
  
  return data.sort((a, b) => b.price - a.price); // Sort by price descending
};
*/


const formatNumber = (num: number): string => {
  return Math.round(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

// Format number with K, M, B abbreviations
const formatNumberWithAbbreviation = (num: number): string => {
  if (num === 0) return '0';

  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1e9) {
    return sign + (absNum / 1e9).toFixed(1) + 'B';
  } else if (absNum >= 1e6) {
    // User Request: Use K for millions, e.g. 11,111,111 -> 11,111.0K
    const valInK = absNum / 1000;
    return sign + valInK.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'K';
  } else {
    // User Request: Show full number for thousands (e.g. 1264 -> 1,264)
    return Math.round(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
};

// Format number with K, M, B abbreviations (Specifically for TOTAL row to keep compact format)
const formatNumberWithAbbreviationTotals = (num: number): string => {
  if (num === 0) return '0';

  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1e9) {
    return sign + (absNum / 1e9).toFixed(1) + 'B';
  } else if (absNum >= 1e6) {
    return sign + (absNum / 1e6).toFixed(1) + 'M';
  } else if (absNum >= 1e3) {
    return sign + (absNum / 1e3).toFixed(1) + 'K';
  } else {
    return Math.round(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
};

// Format ratio (for BLot/Freq, SLot/Freq, etc.)
const formatRatio = (numerator: number, denominator: number): string => {
  if (denominator === 0 || !denominator) return '';
  const ratio = numerator / denominator;

  if (ratio >= 1e9) {
    return (ratio / 1e9).toFixed(1) + 'B';
  } else if (ratio >= 1e6) {
    // Consistent with formatNumberWithAbbreviation
    const valInK = ratio / 1000;
    return valInK.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'K';
  } else {
    return Math.round(ratio).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
};

// Format ratio for Totals (Compact format)
const formatRatioTotals = (numerator: number, denominator: number): string => {
  if (denominator === 0 || !denominator) return '';
  const ratio = numerator / denominator;
  if (ratio >= 1e6) {
    return (ratio / 1e6).toFixed(1) + 'M';
  } else if (ratio >= 1e3) {
    return (ratio / 1e3).toFixed(1) + 'K';
  } else {
    return ratio.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
};

// Helper for Broker Ratio logic
const getBrokerRatio = (num: number, den: number, isBroker: boolean, formatter: (n: number, d: number) => string) => {
  if (!isBroker) return formatter(num, den);
  if (den === 0 || !den) return '';

  // Both negative -> result is negative string
  if (num < 0 && den < 0) {
    const val = formatter(Math.abs(num), Math.abs(den));
    return val ? `-${val}` : '';
  }

  // Mixed signs -> empty
  if ((num < 0 && den > 0) || (num > 0 && den < 0)) {
    return '';
  }

  // Otherwise (both positive or one is zero) -> standard
  return formatter(num, den);
};



// Helper function to get color class based on comparison
// Returns: 'text-green-500' for greater, 'text-red-500' for smaller, 'text-yellow-500' for equal
const getComparisonColor = (buyValue: number, sellValue: number, isBuy: boolean, isBroker: boolean = false): string => {
  const v1 = isBroker ? Math.abs(buyValue) : buyValue;
  const v2 = isBroker ? Math.abs(sellValue) : sellValue;

  if (v1 === v2) return 'text-yellow-500';
  if (isBuy) {
    return v1 > v2 ? 'text-green-500' : 'text-red-500';
  } else {
    return v2 > v1 ? 'text-green-500' : 'text-red-500';
  }
};


// Helper function to calculate totals
const calculateTotals = (data: PriceData[]) => {
  const totals = data.reduce((acc, row) => ({
    bFreq: acc.bFreq + row.bFreq,
    bLot: acc.bLot + row.bLot,
    bOrd: acc.bOrd + row.bOrd,
    sLot: acc.sLot + row.sLot,
    sFreq: acc.sFreq + row.sFreq,
    sOrd: acc.sOrd + row.sOrd,
    tFreq: 0,
    tLot: 0,
    tOrd: 0
  }), { bFreq: 0, bLot: 0, bOrd: 0, sLot: 0, sFreq: 0, sOrd: 0, tFreq: 0, tLot: 0, tOrd: 0 });

  totals.tFreq = totals.bFreq + totals.sFreq;
  totals.tLot = totals.bLot + totals.sLot;
  totals.tOrd = totals.bOrd + totals.sOrd;
  return totals;
};

// Helper function to get all unique prices across all dates (sorted from highest to lowest)
// Returns prices sorted in descending order
const getAllUniquePrices = (_stock: string, dates: string[], stockPriceDataByDate: { [date: string]: PriceData[] }, ohlcData?: any[]): number[] => {
  const priceSet = new Set<number>();
  let minLow = Infinity;
  let maxHigh = -Infinity;

  // Collect all unique prices from all dates
  dates.forEach(date => {
    const data = stockPriceDataByDate[date] || [];
    data.forEach(item => {
      if (item.price) {
        priceSet.add(item.price);
      }
    });

    // Also include high/low prices from OHLC data if provided
    if (ohlcData) {
      const ohlc = ohlcData.find(row => {
        const rowDate = new Date(row.time * 1000).toISOString().split('T')[0];
        return rowDate === date;
      });
      if (ohlc) {
        if (ohlc.high) {
          priceSet.add(ohlc.high);
          if (ohlc.high > maxHigh) maxHigh = ohlc.high;
        }
        if (ohlc.low) {
          priceSet.add(ohlc.low);
          if (ohlc.low < minLow) minLow = ohlc.low;
        }
        if (ohlc.open) priceSet.add(ohlc.open);
        if (ohlc.close) priceSet.add(ohlc.close);
      }
    }
  });

  // Fill in all price steps between global minLow and maxHigh based on IDX tick rules
  if (minLow !== Infinity && maxHigh !== -Infinity && minLow < maxHigh) {
    let currentPrice = minLow;
    // Safety counter to prevent infinite loops
    let iterations = 0;
    const maxIterations = 5000; // More than enough for any reasonable price range

    while (currentPrice <= maxHigh && iterations < maxIterations) {
      priceSet.add(currentPrice);

      // Determine tick size based on current price range (IDX rules)
      let tick = 1;
      if (currentPrice < 200) tick = 1;
      else if (currentPrice < 500) tick = 2;
      else if (currentPrice < 2000) tick = 5;
      else if (currentPrice < 5000) tick = 10;
      else tick = 25;

      currentPrice += tick;
      iterations++;
    }
  }

  // Convert to array and sort from highest to lowest
  return Array.from(priceSet).sort((a, b) => b - a);
};




// Helper function to get broker data for specific price, broker and date (DEPRECATED - moved inside component)
// const getBrokerDataForPriceBrokerAndDate = (stock: string, date: string, price: number, broker: string, brokerDataByDate: { [date: string]: BrokerBreakdownData[] }): BrokerBreakdownData | null => {
//   const data = brokerDataByDate[date] || [];
//   return data.find(item => item.price === price && item.broker === broker) || null;
// };

// Helper function to get-broker data for specific price, broker and date (DEPRECATED)
/*const getBrokerDataForPriceBrokerAndDate = (stock: string, date: string, price: number, broker: string): BrokerBreakdownData | null => {
  // This function is deprecated and will be replaced with in-component function
  return null;
};*/

// Helper function to get all unique price-broker combinations that have transactions (DEPRECATED - not used anymore)
/*const getAllUniquePriceBrokerCombinations = (stock: string, dates: string[]): Array<{price: number, broker: string}> => {
  const combinations = new Map<string, {price: number, broker: string}>();
  
  // First, collect all possible combinations from all dates
  dates.forEach(date => {
    const data = generateBrokerBreakdownData(stock, date);
    data.forEach(item => {
      const key = `${item.price}-${item.broker}`;
      if (!combinations.has(key)) {
        combinations.set(key, { price: item.price, broker: item.broker });
      }
    });
  });
  
  // Filter out combinations that have no transactions across ALL dates
  const validCombinations = Array.from(combinations.values()).filter(combination => {
    // Check if this combination has at least one non-zero transaction across all dates
    let hasAnyTransaction = false;
    
    for (const date of dates) {
      const data = null; // Deprecated function
      if (data && (
        data.bFreq > 0 || data.bLot > 0 || data.sLot > 0 || 
        data.sFreq > 0 || data.tFreq > 0 || data.tLot > 0
      )) {
        hasAnyTransaction = true;
        break; // Found at least one transaction, this combination is valid
      }
    }
    
    return hasAnyTransaction;
  });
  
  // Additional filtering: Remove combinations that only appear in generateBrokerBreakdownData 
  // but have zero values across all dates
  const finalValidCombinations = validCombinations.filter(combination => {
    let totalTransactions = 0;
    
    for (const date of dates) {
      const data = null; // Deprecated function
      if (data) {
        totalTransactions += data.bFreq + data.bLot + data.sLot + data.sFreq + data.tFreq + data.tLot;
      }
    }
    
    return totalTransactions > 0;
  });
  
  // Sort by price ascending, then by broker
  return finalValidCombinations.sort((a, b) => {
    if (a.price !== b.price) return a.price - b.price;
    return a.broker.localeCompare(b.broker);
  });
};*/

// Helper function to find max values for broker breakdown horizontal layout (DEPRECATED)
/*const findMaxValuesBrokerHorizontal = (stock: string, dates: string[]) => {
  let maxBFreq = 0, maxBLot = 0, maxSLot = 0, maxSFreq = 0, maxTFreq = 0, maxTLot = 0;
  
  dates.forEach(date => {
    const data = generateBrokerBreakdownData(stock, date);
    data.forEach(item => {
      if (item.bFreq > maxBFreq) maxBFreq = item.bFreq;
      if (item.bLot > maxBLot) maxBLot = item.bLot;
      if (item.sLot > maxSLot) maxSLot = item.sLot;
      if (item.sFreq > maxSFreq) maxSFreq = item.sFreq;
      if (item.tFreq > maxTFreq) maxTFreq = item.tFreq;
      if (item.tLot > maxTLot) maxTLot = item.tLot;
    });
  });
  
  return { maxBFreq, maxBLot, maxSLot, maxSFreq, maxTFreq, maxTLot };
};*/

// Get trading days based on count (excluding today)
const getTradingDays = (count: number): string[] => {
  const dates: string[] = [];
  const today = new Date();
  let currentDate = new Date(today);

  // Start from yesterday (exclude today)
  currentDate.setDate(currentDate.getDate() - 1);

  while (dates.length < count) {
    const dayOfWeek = currentDate.getDay();

    // Skip weekends (Saturday = 6, Sunday = 0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      const dateStr = currentDate.toISOString().split('T')[0];
      if (dateStr) {
        dates.push(dateStr);
      }
    }

    // Go to previous day
    currentDate.setDate(currentDate.getDate() - 1);

    // Safety check
    if (dates.length === 0 && currentDate.getTime() < today.getTime() - (30 * 24 * 60 * 60 * 1000)) {
      const yesterdayStr = new Date(today.getTime() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      if (yesterdayStr) {
        dates.push(yesterdayStr);
      }
      break;
    }
  }

  return dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
};

const getLastThreeTradingDays = (): string[] => {
  return getTradingDays(3);
};

const formatDisplayDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
};

// Page ID for menu preferences
const PAGE_ID = 'stock-transaction-done-summary';

// Interface for user preferences
interface UserPreferences {
  selectedStocks: string[];
  selectedBrokers: string[];
  invFilter: 'F' | 'D' | '';
  boardFilter: 'RG' | 'TN' | 'NG' | '';
  showFrequency: boolean;
  showOrdColumns: boolean;
  startDate?: string;
  endDate?: string;
}

// Import menu preferences service
import { menuPreferencesService } from '../../services/menuPreferences';

// Utility functions for saving/loading preferences (now using cookies)
const loadPreferences = (pageId: string): Partial<UserPreferences> | null => {
  try {
    const cached = menuPreferencesService.getCachedPreferences(pageId);
    if (cached) {
      return cached as Partial<UserPreferences>;
    }
    const prefs = menuPreferencesService.loadPreferences(pageId);
    return prefs ? prefs as Partial<UserPreferences> : null;
  } catch (error) {
    console.warn('Failed to load cached preferences:', error);
  }
  return null;
};

const savePreferences = (pageId: string, prefs: Partial<UserPreferences>) => {
  menuPreferencesService.savePreferences(pageId, prefs);
};

interface PriceRowProps {
  price: number;
  visibleDates: string[];
  dataByDateMap: Map<string, Map<number, PriceData>>;
  rowTotal: PriceData;
  ohlcData: any[] | undefined;
  prevCloseByDate: Map<string, number | null>;
  showOrdColumns: boolean;
  showFrequency: boolean;
  priceIndex: number;
  totalPrices: number;
  isBrokerBreakdown: boolean;
}

const PriceRow = React.memo(({
  price,
  visibleDates,
  dataByDateMap,
  rowTotal,
  ohlcData,
  prevCloseByDate,
  showOrdColumns,
  showFrequency,
  priceIndex,
  totalPrices,
  isBrokerBreakdown
}: PriceRowProps) => {
  return (
    <tr className={`hover:bg-accent/50 ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`}>
      {visibleDates.map((date, dateIndex) => {
        const data = dataByDateMap.get(date)?.get(price) || null;

        // Calculate background and wick styling based on OHLC
        let bgClass = '';
        const ohlc = ohlcData?.find(row => {
          const rowDate = new Date(row.time * 1000).toISOString().split('T')[0];
          return rowDate === date;
        });

        if (ohlc) {
          if (ohlc.close >= ohlc.open && price >= ohlc.open && price <= ohlc.close) {
            bgClass = 'bg-green-500/20';
          } else if (ohlc.close < ohlc.open && price >= ohlc.close && price <= ohlc.open) {
            bgClass = 'bg-red-500/20';
          }
        }

        const isWick = ohlc && (
          (price >= ohlc.low && price < Math.min(ohlc.open, ohlc.close)) ||
          (price > Math.max(ohlc.open, ohlc.close) && price <= ohlc.high)
        );
        const wickBorderClassL = isWick ? 'border-l-2 border-gray-500' : '';
        const wickBorderClassR = isWick ? 'border-r-2 border-gray-500' : '';

        let ohlcBorderClass = '';
        let hasBottomOhlcBorder = false;
        let hasTopOhlcBorder = false;
        if (ohlc) {
          if (ohlc.close < ohlc.open) {
            if (price === ohlc.open) { ohlcBorderClass = 'border-t-2 border-t-gray-500'; hasTopOhlcBorder = true; }
            else if (price === ohlc.close) { ohlcBorderClass = 'border-b-2 border-b-gray-500'; hasBottomOhlcBorder = true; }
          } else {
            if (price === ohlc.open && price === ohlc.close) {
              ohlcBorderClass = 'border-t-2 border-t-gray-500 border-b-2 border-b-gray-500';
              hasTopOhlcBorder = true; hasBottomOhlcBorder = true;
            } else if (price === ohlc.open) { ohlcBorderClass = 'border-b-2 border-b-gray-500'; hasBottomOhlcBorder = true; }
            else if (price === ohlc.close) { ohlcBorderClass = 'border-t-2 border-t-gray-500'; hasTopOhlcBorder = true; }
          }
        }

        const cellClass = (align: string, border: string, textColor: string = 'text-white') => {
          let finalBorder = border;
          if (hasBottomOhlcBorder) finalBorder = finalBorder.replace('border-b-2 border-white', '');
          if (hasTopOhlcBorder) finalBorder = finalBorder.replace('border-t-2 border-white', '');
          return `${align} py-[1px] px-[4px] font-bold ${bgClass} ${textColor} ${finalBorder} ${ohlcBorderClass}`;
        };

        const prevClose = prevCloseByDate.get(date);
        let priceColorClass = 'text-white';
        if (prevClose) {
          if (price > prevClose) priceColorClass = 'text-green-500';
          else if (price < prevClose) priceColorClass = 'text-red-500';
          else priceColorClass = 'text-yellow-500';
        }

        if (!data) {
          return (
            <React.Fragment key={date}>
              <td className={`text-center py-[1px] px-[6px] font-bold ${bgClass} ${priceColorClass} ${dateIndex === 0 ? 'border-l-2 border-white' : ''} ${priceIndex === totalPrices - 1 && !hasBottomOhlcBorder ? 'border-b-2 border-white' : ''} ${ohlcBorderClass}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatNumber(price)}
              </td>
              {showOrdColumns && (
                <>
                  <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '')}></td>
                  <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '')}></td>
                </>
              )}
              {showFrequency && <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '')}></td>}
              {showFrequency && <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '')}></td>}
              <td className={cellClass('text-right', `${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''} ${wickBorderClassR}`)}></td>
              <td className={cellClass('text-right', `${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''} ${wickBorderClassL}`)}></td>
              {showFrequency && <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '')}></td>}
              {showFrequency && <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '')}></td>}
              {showOrdColumns && (
                <>
                  <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '')}></td>
                  <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '')}></td>
                </>
              )}
              {showFrequency && <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '')}></td>}
              {showOrdColumns && (
                <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '')}></td>
              )}
              <td className={cellClass('text-right', `${dateIndex < visibleDates.length - 1 ? 'border-r-[10px] border-r-white' : ''} ${dateIndex === visibleDates.length - 1 ? 'border-r-[10px] border-r-white' : ''} ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`)}></td>
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={date}>
            <td className={`text-center py-[1px] px-[6px] font-bold ${bgClass} ${priceColorClass} ${dateIndex === 0 ? 'border-l-2 border-white' : ''} ${priceIndex === totalPrices - 1 && !hasBottomOhlcBorder ? 'border-b-2 border-white' : ''} ${ohlcBorderClass}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatNumber(price)}
            </td>
            {showOrdColumns && (
              <>
                <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '', 'text-slate-400')} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {getBrokerRatio(data.bLot, data.bOrd, isBrokerBreakdown, formatRatio)}
                </td>
                <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '', getComparisonColor(data.bOrd, data.sOrd, true, isBrokerBreakdown))} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatNumberWithAbbreviation(data.bOrd)}
                </td>
              </>
            )}
            {showFrequency && (
              <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '', 'text-slate-400')} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {getBrokerRatio(data.bLot, data.bFreq, isBrokerBreakdown, formatRatio)}
              </td>
            )}
            {showFrequency && (
              <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '', getComparisonColor(data.bFreq, data.sFreq, true, isBrokerBreakdown))} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatNumberWithAbbreviation(data.bFreq)}
              </td>
            )}
            <td className={cellClass('text-right', `${priceIndex === totalPrices - 1 && !isWick ? 'border-b-2 border-white' : ''} ${wickBorderClassR}`)} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatNumber(data.bLot)}
            </td>
            <td className={cellClass('text-right', `${priceIndex === totalPrices - 1 && !isWick ? 'border-b-2 border-white' : ''} ${wickBorderClassL}`)} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatNumber(data.sLot)}
            </td>
            {showFrequency && (
              <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '', getComparisonColor(data.bFreq, data.sFreq, false, isBrokerBreakdown))} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatNumberWithAbbreviation(data.sFreq)}
              </td>
            )}
            {showFrequency && (
              <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '', 'text-slate-400')} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {getBrokerRatio(data.sLot, data.sFreq, isBrokerBreakdown, formatRatio)}
              </td>
            )}
            {showOrdColumns && (
              <>
                <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '', getComparisonColor(data.bOrd, data.sOrd, false, isBrokerBreakdown))} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatNumberWithAbbreviation(data.sOrd)}
                </td>
                <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '', 'text-slate-400')} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {getBrokerRatio(data.sLot, data.sOrd, isBrokerBreakdown, formatRatio)}
                </td>
              </>
            )}
            {showFrequency && (
              <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '', 'text-white')} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatNumberWithAbbreviation(data.tFreq)}
              </td>
            )}
            {showOrdColumns && (
              <td className={cellClass('text-right', priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : '', 'text-white')} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatNumberWithAbbreviation(data.tOrd)}
              </td>
            )}
            <td className={cellClass('text-right', `${dateIndex < visibleDates.length - 1 ? 'border-r-[10px] border-r-white' : ''} ${dateIndex === visibleDates.length - 1 ? 'border-r-[10px] border-r-white' : ''} ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`, 'text-white')} style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatNumberWithAbbreviation(data.tLot)}
            </td>
          </React.Fragment>
        );
      })}

      {/* Row Grand Totals */}
      <td className={`text-center py-[1px] px-[5px] font-bold text-white ${visibleDates.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'} ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatNumber(price)}
      </td>
      {showOrdColumns && (
        <>
          <td className={`text-right py-[1px] px-[5px] font-bold text-slate-400 ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {getBrokerRatio(rowTotal.bLot, rowTotal.bOrd, isBrokerBreakdown, formatRatio)}
          </td>
          <td className={`text-right py-[1px] px-[5px] font-bold ${getComparisonColor(rowTotal.bOrd, rowTotal.sOrd, true, isBrokerBreakdown)} ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatNumberWithAbbreviation(rowTotal.bOrd)}
          </td>
        </>
      )}
      {showFrequency && (
        <td className={`text-right py-[1px] px-[5px] font-bold text-slate-400 ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {getBrokerRatio(rowTotal.bLot, rowTotal.bFreq, isBrokerBreakdown, formatRatio)}
        </td>
      )}
      {showFrequency && (
        <td className={`text-right py-[1px] px-[5px] font-bold ${getComparisonColor(rowTotal.bFreq, rowTotal.sFreq, true, isBrokerBreakdown)} ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatNumberWithAbbreviation(rowTotal.bFreq)}
        </td>
      )}
      <td className={`text-right py-[1px] px-[5px] font-bold text-white ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatNumber(rowTotal.bLot)}
      </td>
      <td className={`text-right py-[1px] px-[5px] font-bold text-white ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatNumber(rowTotal.sLot)}
      </td>
      {showFrequency && (
        <td className={`text-right py-[1px] px-[5px] font-bold ${getComparisonColor(rowTotal.bFreq, rowTotal.sFreq, false, isBrokerBreakdown)} ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatNumberWithAbbreviation(rowTotal.sFreq)}
        </td>
      )}
      {showFrequency && (
        <td className={`text-right py-[1px] px-[5px] font-bold text-slate-400 ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {getBrokerRatio(rowTotal.sLot, rowTotal.sFreq, isBrokerBreakdown, formatRatio)}
        </td>
      )}
      {showOrdColumns && (
        <>
          <td className={`text-right py-[1px] px-[5px] font-bold ${getComparisonColor(rowTotal.bOrd, rowTotal.sOrd, false, isBrokerBreakdown)} ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatNumberWithAbbreviation(rowTotal.sOrd)}
          </td>
          <td className={`text-right py-[1px] px-[5px] font-bold text-slate-400 ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {getBrokerRatio(rowTotal.sLot, rowTotal.sOrd, isBrokerBreakdown, formatRatio)}
          </td>
        </>
      )}
      {showFrequency && (
        <td className={`text-right py-[1px] px-[5px] font-bold text-white ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatNumberWithAbbreviation(rowTotal.tFreq)}
        </td>
      )}
      {showOrdColumns && (
        <td className={`text-right py-[1px] px-[7px] font-bold text-white ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatNumberWithAbbreviation(rowTotal.tOrd)}
        </td>
      )}
      <td className={`text-right py-[1px] px-[5px] font-bold text-white border-r-2 border-white ${priceIndex === totalPrices - 1 ? 'border-b-2 border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatNumberWithAbbreviation(rowTotal.tLot)}
      </td>
    </tr>
  );
});

interface StockSummaryTableProps {
  stock: string;
  visibleDates: string[];
  processedData: any;
  ohlcData: any[] | undefined;
  showOrdColumns: boolean;
  showFrequency: boolean;
  selectedBrokers: string[];
  selectedStocksCount: number;
}

const StockSummaryTable = React.memo(({
  stock,
  visibleDates,
  processedData,
  ohlcData,
  showOrdColumns,
  showFrequency,
  selectedBrokers,
  selectedStocksCount
}: StockSummaryTableProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const { allPrices, dataByDateMap, rowTotals, dateTotals, grandTotals } = processedData;

  const isBrokerBreakdown = selectedBrokers.length > 0;

  const prevCloseByDate = useMemo(() => {
    const map = new Map<string, number | null>();
    if (!ohlcData || ohlcData.length === 0) return map;

    visibleDates.forEach(date => {
      let prevRow: any = null;
      let maxPrevDate = '';
      for (const r of ohlcData) {
        const rDate = new Date(r.time * 1000).toISOString().split('T')[0];
        if (rDate < date && (!prevRow || rDate > maxPrevDate)) {
          prevRow = r;
          maxPrevDate = rDate;
        }
      }
      map.set(date, prevRow?.close > 0 ? prevRow.close : null);
    });
    return map;
  }, [ohlcData, visibleDates]);

  const baseCols = 4;
  const freqCols = showFrequency ? 5 : 0;
  const ordCols = showOrdColumns ? 5 : 0;
  const colSpan = baseCols + freqCols + ordCols;

  const colWidths = useMemo(() => {
    const base = {
      price: 50, blotO: 55, bOr: 45, blotF: 50, bfreq: 45, blot: 75,
      slot: 75, sfreq: 45, slotF: 50, sOr: 45, slotO: 50,
      tfreq: 45, tOr: 45, tlot: 80
    };

    const needsExtra = {
      price: false, blotO: false, bOr: false, blotF: false, bfreq: false, blot: false,
      slot: false, sfreq: false, slotF: false, sOr: false, slotO: false,
      tfreq: true, tOr: true, tlot: true
    };

    const check = (val: number, formatter: any, key: keyof typeof needsExtra) => {
      if (needsExtra[key]) return;
      const str = formatter(val);
      if (str.length > 6) needsExtra[key] = true;
    };

    const checkRatio = (n: number, d: number, formatter: any, key: keyof typeof needsExtra) => {
      if (needsExtra[key]) return;
      const str = formatter(n, d);
      if (str.length > 6) needsExtra[key] = true;
    };

    // Check all data rows and totals
    allPrices.forEach((p: number) => {
      const rt = rowTotals.get(p);
      if (rt) {
        check(p, formatNumber, 'price');
        check(rt.bLot, formatNumber, 'blot');
        check(rt.sLot, formatNumber, 'slot');
        checkRatio(rt.bLot, rt.bOrd, formatRatio, 'blotO');
        check(rt.bOrd, formatNumberWithAbbreviation, 'bOr');
        checkRatio(rt.bLot, rt.bFreq, formatRatio, 'blotF');
        check(rt.bFreq, formatNumberWithAbbreviation, 'bfreq');
        check(rt.sFreq, formatNumberWithAbbreviation, 'sfreq');
        checkRatio(rt.sLot, rt.sFreq, formatRatio, 'slotF');
        check(rt.sOrd, formatNumberWithAbbreviation, 'sOr');
        checkRatio(rt.sLot, rt.sOrd, formatRatio, 'slotO');
        check(rt.tFreq, formatNumberWithAbbreviation, 'tfreq');
        check(rt.tOrd, formatNumberWithAbbreviation, 'tOr');
        check(rt.tLot, formatNumberWithAbbreviation, 'tlot');
      }
      visibleDates.forEach(d => {
        const data = dataByDateMap.get(d)?.get(p);
        if (data) {
          check(data.bLot, formatNumber, 'blot');
          check(data.sLot, formatNumber, 'slot');
          checkRatio(data.bLot, data.bOrd, formatRatio, 'blotO');
          check(data.bOrd, formatNumberWithAbbreviation, 'bOr');
          checkRatio(data.bLot, data.bFreq, formatRatio, 'blotF');
          check(data.bFreq, formatNumberWithAbbreviation, 'bfreq');
          check(data.sFreq, formatNumberWithAbbreviation, 'sfreq');
          checkRatio(data.sLot, data.sFreq, formatRatio, 'slotF');
          check(data.sOrd, formatNumberWithAbbreviation, 'sOr');
          checkRatio(data.sLot, data.sOrd, formatRatio, 'slotO');
          check(data.tFreq, formatNumberWithAbbreviation, 'tfreq');
          check(data.tOrd, formatNumberWithAbbreviation, 'tOr');
          check(data.tLot, formatNumberWithAbbreviation, 'tlot');
        }
      });
    });

    // Check date totals footer
    dateTotals.forEach((t: any) => {
      check(t.bLot, formatNumberWithAbbreviationTotals, 'blot');
      check(t.sLot, formatNumberWithAbbreviationTotals, 'slot');
      checkRatio(t.bLot, t.bOrd, formatRatioTotals, 'blotO');
      check(t.bOrd, formatNumberWithAbbreviationTotals, 'bOr');
      checkRatio(t.bLot, t.bFreq, formatRatioTotals, 'blotF');
      check(t.bFreq, formatNumberWithAbbreviationTotals, 'bfreq');
      check(t.sFreq, formatNumberWithAbbreviationTotals, 'sfreq');
      checkRatio(t.sLot, t.sFreq, formatRatioTotals, 'slotF');
      check(t.sOrd, formatNumberWithAbbreviationTotals, 'sOr');
      checkRatio(t.sLot, t.sOrd, formatRatioTotals, 'slotO');
      check(t.tFreq, formatNumberWithAbbreviationTotals, 'tfreq');
      check(t.tOrd, formatNumberWithAbbreviationTotals, 'tOr');
      check(t.tLot, formatNumberWithAbbreviationTotals, 'tlot');
    });

    // Check grand totals
    check(grandTotals.bLot, formatNumberWithAbbreviationTotals, 'blot');
    check(grandTotals.sLot, formatNumberWithAbbreviationTotals, 'slot');
    checkRatio(grandTotals.bLot, grandTotals.bOrd, formatRatioTotals, 'blotO');
    check(grandTotals.bOrd, formatNumberWithAbbreviationTotals, 'bOr');
    checkRatio(grandTotals.bLot, grandTotals.bFreq, formatRatioTotals, 'blotF');
    check(grandTotals.bFreq, formatNumberWithAbbreviationTotals, 'bfreq');
    check(grandTotals.sFreq, formatNumberWithAbbreviationTotals, 'sfreq');
    checkRatio(grandTotals.sLot, grandTotals.sFreq, formatRatioTotals, 'slotF');
    check(grandTotals.sOrd, formatNumberWithAbbreviationTotals, 'sOr');
    checkRatio(grandTotals.sLot, grandTotals.sOrd, formatRatioTotals, 'slotO');
    check(grandTotals.tFreq, formatNumberWithAbbreviation, 'tfreq');
    check(grandTotals.tOrd, formatNumberWithAbbreviation, 'tOr');
    check(grandTotals.tLot, formatNumberWithAbbreviation, 'tlot');

    return {
      price: base.price + (needsExtra.price ? 5 : 0),
      blotO: base.blotO + (needsExtra.blotO ? 5 : 0),
      bOr: base.bOr + (needsExtra.bOr ? 5 : 0),
      blotF: base.blotF + (needsExtra.blotF ? 5 : 0),
      bfreq: base.bfreq + (needsExtra.bfreq ? 5 : 0),
      blot: base.blot + (needsExtra.blot ? 5 : 0),
      slot: base.slot + (needsExtra.slot ? 5 : 0),
      sfreq: base.sfreq + (needsExtra.sfreq ? 5 : 0),
      slotF: base.slotF + (needsExtra.slotF ? 5 : 0),
      sOr: base.sOr + (needsExtra.sOr ? 5 : 0),
      slotO: base.slotO + (needsExtra.slotO ? 5 : 0),
      tfreq: base.tfreq + (needsExtra.tfreq ? 5 : 0),
      tOr: base.tOr + (needsExtra.tOr ? 5 : 0),
      tlot: base.tlot + (needsExtra.tlot ? 5 : 0)
    };
  }, [allPrices, rowTotals, dataByDateMap, dateTotals, grandTotals, visibleDates]);

  const getBlockWidth = () => {
    let width = colWidths.price + colWidths.blot + colWidths.slot + colWidths.tlot;
    if (showOrdColumns) width += colWidths.blotO + colWidths.bOr + colWidths.sOr + colWidths.slotO + colWidths.tOr;
    if (showFrequency) width += colWidths.blotF + colWidths.bfreq + colWidths.sfreq + colWidths.slotF + colWidths.tfreq;
    return width;
  };

  const blockWidth = getBlockWidth();
  const tableWidth = blockWidth * (visibleDates.length + 1);

  return (
    <Card key={stock} className="overflow-hidden rounded-none border-[#1e293b]">
      {(selectedStocksCount > 1 || selectedBrokers.length > 0) && (
        <CardHeader 
          className={`${selectedStocksCount > 1 ? 'cursor-pointer hover:bg-accent/50 transition-colors' : ''} select-none p-0 px-4 sm:px-6 h-auto flex flex-row items-center whitespace-nowrap ${isExpanded ? 'pt-2 pb-0' : 'py-2'}`} 
          onClick={() => selectedStocksCount > 1 && setIsExpanded(!isExpanded)}
        >
          <CardTitle className="flex items-center gap-2 w-full text-left leading-none m-0">
            {selectedStocksCount > 1 && (
              <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? '' : '-rotate-90'}`} />
            )}
            {selectedStocksCount > 1 && (
              selectedBrokers.length > 0
                ? `${stock} - ${selectedBrokers.join(', ')}`
                : stock
            )}
            {selectedStocksCount === 1 && selectedBrokers.length > 0 && selectedBrokers.join(', ')}
          </CardTitle>
        </CardHeader>
      )}
      {isExpanded && (
        <CardContent className={selectedStocksCount > 1 || selectedBrokers.length > 0 ? "p-0 sm:px-6 sm:pb-6" : "p-4 sm:p-6"}>
          <div className="overflow-x-auto">
          <div className="px-4 sm:px-0 pb-4 w-fit">
            <table className="text-[12px] border-collapse" style={{ tableLayout: 'fixed', width: `${tableWidth}px` }}>
              <colgroup>
                {[...visibleDates, 'TOTAL'].map((_, i) => (
                  <React.Fragment key={i}>
                    <col style={{ width: `${colWidths.price}px` }} />
                    {showOrdColumns && (
                      <>
                        <col style={{ width: `${colWidths.blotO}px` }} />
                        <col style={{ width: `${colWidths.bOr}px` }} />
                      </>
                    )}
                    {showFrequency && (
                      <>
                        <col style={{ width: `${colWidths.blotF}px` }} />
                        <col style={{ width: `${colWidths.bfreq}px` }} />
                      </>
                    )}
                    <col style={{ width: `${colWidths.blot}px` }} />
                    <col style={{ width: `${colWidths.slot}px` }} />
                    {showFrequency && (
                      <>
                        <col style={{ width: `${colWidths.sfreq}px` }} />
                        <col style={{ width: `${colWidths.slotF}px` }} />
                      </>
                    )}
                    {showOrdColumns && (
                      <>
                        <col style={{ width: `${colWidths.sOr}px` }} />
                        <col style={{ width: `${colWidths.slotO}px` }} />
                      </>
                    )}
                    {showFrequency && <col style={{ width: `${colWidths.tfreq}px` }} />}
                    {showOrdColumns && <col style={{ width: `${colWidths.tOr}px` }} />}
                    <col style={{ width: `${colWidths.tlot}px` }} />
                  </React.Fragment>
                ))}
              </colgroup>
              <thead>
                <tr className="border-t-2 border-white bg-[#3a4252]">
                  {visibleDates.map((date, dateIndex) => (
                    <th key={date} colSpan={colSpan} className={`text-center py-[1px] px-[8.24px] font-bold text-white whitespace-nowrap ${dateIndex === 0 ? 'border-l-2 border-white' : ''} ${dateIndex < visibleDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === visibleDates.length - 1 ? 'border-r-[10px] border-white' : ''}`}>
                      {formatDisplayDate(date)}
                    </th>
                  ))}
                  <th colSpan={colSpan} className={`text-center py-[1px] px-[4.2px] font-bold text-white border-r-2 border-white ${visibleDates.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'}`}>
                    Total
                  </th>
                </tr>
                <tr className="bg-[#3a4252]">
                  {visibleDates.map((date, dateIndex) => (
                    <React.Fragment key={date}>
                      <th className={`text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`}>Price</th>
                      {showOrdColumns && (
                        <>
                          <th className="text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap">BLot/O</th>
                          <th className="text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap">BOrd</th>
                        </>
                      )}
                      {showFrequency && <th className="text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap">BLot/F</th>}
                      {showFrequency && <th className="text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap">BFreq</th>}
                      <th className="text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap">BLot</th>
                      <th className="text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap">SLot</th>
                      {showFrequency && <th className="text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap">SFreq</th>}
                      {showFrequency && <th className="text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap">SLot/F</th>}
                      {showOrdColumns && (
                        <>
                          <th className="text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap">SOrd</th>
                          <th className="text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap">SLot/O</th>
                        </>
                      )}
                      {showFrequency && <th className="text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap">TFreq</th>}
                      {showOrdColumns && (
                        <th className={`text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap`}>TOr</th>
                      )}
                      <th className={`text-center py-[1px] px-[4px] font-bold text-white whitespace-nowrap ${dateIndex < visibleDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === visibleDates.length - 1 ? 'border-r-[10px] border-white' : ''}`}>TLot</th>
                    </React.Fragment>
                  ))}
                  <th className={`text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap ${visibleDates.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'}`}>Price</th>
                  {showOrdColumns && (
                    <>
                      <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">BLot/O</th>
                      <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">BOrd</th>
                    </>
                  )}
                  {showFrequency && <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">BLot/F</th>}
                  {showFrequency && <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">BFreq</th>}
                  <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">BLot</th>
                  <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">SLot</th>
                  {showFrequency && <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">SFreq</th>}
                  {showFrequency && <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">SLot/F</th>}
                  {showOrdColumns && (
                    <>
                      <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">SOrd</th>
                      <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">SLot/O</th>
                    </>
                  )}
                  {showFrequency && <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">TFreq</th>}
                  {showOrdColumns ? (
                    <>
                      <th className="text-center py-[1px] px-[3px] font-bold text-white whitespace-nowrap">TOr</th>
                      <th className="text-center py-[1px] px-[5px] font-bold text-white whitespace-nowrap border-r-2 border-white">TLot</th>
                    </>
                  ) : (
                    <th className="text-center py-[1px] px-[5px] font-bold text-white whitespace-nowrap border-r-2 border-white">TLot</th>
                  )}
                </tr>
              </thead>
              <tbody className="text-[12px]">
                {allPrices.map((price: number, idx: number) => (
                  <PriceRow
                    key={`${stock}-${price}`}
                    price={price}
                    visibleDates={visibleDates}
                    dataByDateMap={dataByDateMap}
                    rowTotal={rowTotals.get(price)!}
                    ohlcData={ohlcData}
                    prevCloseByDate={prevCloseByDate}
                    showOrdColumns={showOrdColumns}
                    showFrequency={showFrequency}
                    priceIndex={idx}
                    totalPrices={allPrices.length}
                    isBrokerBreakdown={isBrokerBreakdown}
                  />
                ))}
                {/* Total Row */}
                <tr className="border-t-2 border-b-2 border-white font-bold">
                  {visibleDates.map((date, dateIndex) => {
                    const totals = dateTotals.get(date);
                    return (
                      <React.Fragment key={date}>
                        <td className={`text-center py-[1px] px-[6px] font-bold text-white ${dateIndex === 0 ? 'border-l-2 border-white' : ''}`}></td>
                        {showOrdColumns && (
                          <>
                            <td className={`text-right py-[1px] px-[6px] font-bold text-slate-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {getBrokerRatio(totals.bLot, totals.bOrd, isBrokerBreakdown, formatRatioTotals)}
                            </td>
                            <td className={`text-right py-[1px] px-[6px] font-bold ${getComparisonColor(totals.bOrd, totals.sOrd, true, isBrokerBreakdown)}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {formatNumberWithAbbreviationTotals(totals.bOrd)}
                            </td>
                          </>
                        )}
                        {showFrequency && (
                          <td className={`text-right py-[1px] px-[6px] font-bold text-slate-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {getBrokerRatio(totals.bLot, totals.bFreq, isBrokerBreakdown, formatRatioTotals)}
                          </td>
                        )}
                        {showFrequency && (
                          <td className={`text-right py-[1px] px-[6px] font-bold ${getComparisonColor(totals.bFreq, totals.sFreq, true, isBrokerBreakdown)}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatNumberWithAbbreviationTotals(totals.bFreq)}
                          </td>
                        )}
                        <td className="text-right py-[1px] px-[6px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithAbbreviationTotals(totals.bLot)}</td>
                        <td className="text-right py-[1px] px-[6px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithAbbreviationTotals(totals.sLot)}</td>
                        {showFrequency && (
                          <td className={`text-right py-[1px] px-[6px] font-bold ${getComparisonColor(totals.bFreq, totals.sFreq, false, isBrokerBreakdown)}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatNumberWithAbbreviationTotals(totals.sFreq)}
                          </td>
                        )}
                        {showFrequency && (
                          <td className={`text-right py-[1px] px-[6px] font-bold text-slate-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {getBrokerRatio(totals.sLot, totals.sFreq, isBrokerBreakdown, formatRatioTotals)}
                          </td>
                        )}
                        {showOrdColumns && (
                          <>
                            <td className={`text-right py-[1px] px-[6px] font-bold ${getComparisonColor(totals.bOrd, totals.sOrd, false, isBrokerBreakdown)}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {formatNumberWithAbbreviationTotals(totals.sOrd)}
                            </td>
                            <td className={`text-right py-[1px] px-[6px] font-bold text-slate-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {getBrokerRatio(totals.sLot, totals.sOrd, isBrokerBreakdown, formatRatioTotals)}
                            </td>
                          </>
                        )}
                        {showFrequency && <td className="text-right py-[1px] px-[6px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithAbbreviationTotals(totals.tFreq)}</td>}
                        {showOrdColumns && (
                          <td className={`text-right py-[1px] px-[6px] font-bold text-white`} style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithAbbreviationTotals(totals.tOrd)}</td>
                        )}
                        <td className={`text-right py-[1px] px-[6px] font-bold text-white ${dateIndex < visibleDates.length - 1 ? 'border-r-[10px] border-white' : ''} ${dateIndex === visibleDates.length - 1 ? 'border-r-[10px] border-white' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithAbbreviationTotals(totals.tLot)}</td>
                      </React.Fragment>
                    );
                  })}
                  {/* Grand Total Footer Cells */}
                  <td className={`text-center py-[1px] px-[5px] font-bold text-white ${visibleDates.length === 0 ? 'border-l-2 border-white' : 'border-l-[10px] border-white'}`}>TOTAL</td>
                  {showOrdColumns && (
                    <>
                      <td className={`text-right py-[1px] px-[5px] font-bold text-slate-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {getBrokerRatio(grandTotals.bLot, grandTotals.bOrd, isBrokerBreakdown, formatRatioTotals)}
                      </td>
                      <td className={`text-right py-[1px] px-[5px] font-bold ${getComparisonColor(grandTotals.bOrd, grandTotals.sOrd, true, isBrokerBreakdown)}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumberWithAbbreviationTotals(grandTotals.bOrd)}
                      </td>
                    </>
                  )}
                  {showFrequency && (
                    <td className={`text-right py-[1px] px-[5px] font-bold text-slate-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {getBrokerRatio(grandTotals.bLot, grandTotals.bFreq, isBrokerBreakdown, formatRatioTotals)}
                    </td>
                  )}
                  {showFrequency && (
                    <td className={`text-right py-[1px] px-[5px] font-bold ${getComparisonColor(grandTotals.bFreq, grandTotals.sFreq, true, isBrokerBreakdown)}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumberWithAbbreviationTotals(grandTotals.bFreq)}
                    </td>
                  )}
                  <td className="text-right py-[1px] px-[5px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithAbbreviationTotals(grandTotals.bLot)}</td>
                  <td className="text-right py-[1px] px-[5px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithAbbreviationTotals(grandTotals.sLot)}</td>
                  {showFrequency && (
                    <td className={`text-right py-[1px] px-[5px] font-bold ${getComparisonColor(grandTotals.bFreq, grandTotals.sFreq, false, isBrokerBreakdown)}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatNumberWithAbbreviationTotals(grandTotals.sFreq)}
                    </td>
                  )}
                  {showFrequency && (
                    <td className={`text-right py-[1px] px-[5px] font-bold text-slate-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {getBrokerRatio(grandTotals.sLot, grandTotals.sFreq, isBrokerBreakdown, formatRatioTotals)}
                    </td>
                  )}
                  {showOrdColumns && (
                    <>
                      <td className={`text-right py-[1px] px-[5px] font-bold ${getComparisonColor(grandTotals.bOrd, grandTotals.sOrd, false, isBrokerBreakdown)}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumberWithAbbreviationTotals(grandTotals.sOrd)}
                      </td>
                      <td className={`text-right py-[1px] px-[5px] font-bold text-slate-400`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {getBrokerRatio(grandTotals.sLot, grandTotals.sOrd, isBrokerBreakdown, formatRatioTotals)}
                      </td>
                    </>
                  )}
                  {showFrequency && <td className="text-right py-[1px] px-[5px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithAbbreviation(grandTotals.tFreq)}</td>}
                  {showOrdColumns ? (
                    <>
                      <td className="text-right py-[1px] px-[5px] font-bold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithAbbreviation(grandTotals.tOrd)}</td>
                      <td className={`text-right py-[1px] px-[7px] font-bold text-white border-r-2 border-white`} style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithAbbreviation(grandTotals.tLot)}</td>
                    </>
                  ) : (
                    <td className={`text-right py-[1px] px-[7px] font-bold text-white border-r-2 border-white`} style={{ fontVariantNumeric: 'tabular-nums' }}>{formatNumberWithAbbreviation(grandTotals.tLot)}</td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    )}
    </Card>
  );
});

interface StockTransactionDoneSummaryProps {
  selectedStock?: string;
  disableTickerSelection?: boolean; // When true, only use propSelectedStock and hide ticker selection UI
  isDashboard?: boolean;
  isActive?: boolean;
}

const GOVERNMENT_BROKERS = ['AN', 'CC', 'DD', 'EE', 'II', 'KK', 'OD', 'PN', 'TP', 'YU', 'ZJ'];
const FOREIGN_BROKERS = ['AK', 'BK', 'CG', 'CS', 'DB', 'DP', 'DX', 'GW', 'KZ', 'MS', 'RX', 'YU', 'ZP'];

const getBrokerColorClass = (brokerCode: string): { color: string; className: string } => {
  if (GOVERNMENT_BROKERS.includes(brokerCode)) {
    return { color: '#10B981', className: 'font-semibold' }; // Green-600
  }
  if (FOREIGN_BROKERS.includes(brokerCode)) {
    return { color: '#EF4444', className: 'font-semibold' }; // Red-600
  }
  return { color: '#FFFFFF', className: 'font-semibold' }; // White
};

interface BrokerSelectorProps {
  selectedBrokers: string[];
  setSelectedBrokers: React.Dispatch<React.SetStateAction<string[]>>;
  availableBrokers: string[];
  isTrial: boolean;
  showToast: (options: { type: 'success' | 'error' | 'warning' | 'info'; title: string; message: string }) => void;
}

function BrokerSelector({
  selectedBrokers,
  setSelectedBrokers,
  availableBrokers,
  isTrial,
  showToast
}: BrokerSelectorProps) {
  const [brokerInput, setBrokerInput] = useState('');
  const [showBrokerSuggestions, setShowBrokerSuggestions] = useState(false);
  const dropdownBrokerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownBrokerRef.current && !dropdownBrokerRef.current.contains(event.target as Node)) {
        setShowBrokerSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredBrokers = useMemo(() => {
    if (!brokerInput) return availableBrokers;
    return availableBrokers.filter(b => b.toLowerCase().includes(brokerInput.toLowerCase()));
  }, [availableBrokers, brokerInput]);

  return (
    <div className="relative w-full md:w-32" ref={dropdownBrokerRef}>
      <div
        className={`w-full md:w-32 h-9 px-3 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground cursor-pointer hover:bg-accent/50 transition-colors ${isTrial ? 'opacity-80' : ''}`}
        onClick={() => {
          if (isTrial) {
            showToast({
              type: 'warning',
              title: 'Fitur Terbatas',
              message: 'Dalam masa free trial, Anda tidak dapat memilih broker spesifik.'
            });
            return;
          }
          setShowBrokerSuggestions(!showBrokerSuggestions);
        }}
      >
        <span className="truncate">
          {selectedBrokers.length === 0 ? 'All' : selectedBrokers.join(', ')}
        </span>
        <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0" />
      </div>

      {!isTrial && showBrokerSuggestions && (
        <div id="broker-suggestions" role="listbox" className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 w-48 overflow-hidden flex flex-col">
          {availableBrokers.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground flex items-center">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Loading brokers...
            </div>
          ) : (
            <>
              {selectedBrokers.length > 0 && !selectedBrokers.includes('ALL') && (
                <div className="px-3 py-2 text-xs text-muted-foreground border-b border-[#3a4252] flex items-center justify-between bg-popover">
                  <span>Selected ({selectedBrokers.length})</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBrokers([]);
                    }}
                    className="text-xs text-destructive hover:text-destructive/80 font-medium"
                  >
                    Clear
                  </button>
                </div>
              )}
              <div className="p-2 border-b border-[#3a4252]">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <input
                    type="text"
                    className="w-full h-8 pl-7 pr-2 bg-[#0a0f20] border border-[#3a4252] rounded text-sm text-white focus:outline-none focus:border-primary/50"
                    placeholder="Search broker..."
                    value={brokerInput}
                    onChange={(e) => setBrokerInput(e.target.value.toUpperCase())}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto py-1">
                {brokerInput === '' && (
                  <div
                    className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${selectedBrokers.includes('ALL') || selectedBrokers.length === 0 ? 'text-primary font-medium' : 'text-white'}`}
                    onClick={() => setSelectedBrokers(['ALL'])}
                  >
                    <span>ALL</span>
                    {selectedBrokers.length === 0 && <span className="text-primary text-xs">✓</span>}
                  </div>
                )}
                {filteredBrokers.slice(0, 30).map(broker => {
                  const isSelected = selectedBrokers.includes(broker);
                  const brokerColor = getBrokerColorClass(broker);
                  return (
                    <div
                      key={broker}
                      className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${isSelected ? 'font-medium bg-accent/50' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isSelected) {
                          setSelectedBrokers(selectedBrokers.filter(b => b !== broker));
                        } else {
                          setSelectedBrokers([...selectedBrokers.filter(b => b !== 'ALL'), broker]);
                        }
                      }}
                    >
                      <span className={brokerColor.className} style={{ color: isSelected ? undefined : brokerColor.color }}>{broker}</span>
                      {isSelected && <span className="text-primary text-xs">✓</span>}
                    </div>
                  );
                })}
                {filteredBrokers.length > 30 && (
                  <div className="px-3 py-1.5 text-xs text-muted-foreground border-t border-[#3a4252] text-center">
                    ... and {filteredBrokers.length - 30} more brokers
                  </div>
                )}
                {filteredBrokers.length === 0 && (
                  <div className="px-3 py-2 text-sm text-muted-foreground text-center">No brokers found</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function StockTransactionDoneSummary({ selectedStock: propSelectedStock, disableTickerSelection = false, isDashboard = false, isActive = true }: StockTransactionDoneSummaryProps) {
  const { showToast } = useToast();
  const { profile } = useProfile();
  const isTrial = profile?.subscriptionStatus === 'trial';

  // Force default restrictions for trial users
  useEffect(() => {
    if (isTrial) {
      setSelectedBrokers(['ALL']);
      setPendingShowOrdColumns(false);
      setShowOrdColumns(false);
    }
  }, [isTrial]);

  // Load preferences from cookies on mount
  const effectivePageId = isDashboard ? `dashboard-${PAGE_ID}` : PAGE_ID;
  const [savedPrefs] = useState(() => loadPreferences(effectivePageId));

  useEffect(() => {
    if (savedPrefs) {
      if (savedPrefs['showFrequency'] !== undefined) {
        setShowFrequency(savedPrefs['showFrequency']);
      }
      if (savedPrefs['showOrdColumns'] !== undefined && !isDashboard) {
        setShowOrdColumns(savedPrefs['showOrdColumns']);
      }
      if (savedPrefs['invFilter'] !== undefined) {
        setInvFilter(savedPrefs['invFilter']);
      }
      if (savedPrefs['boardFilter'] !== undefined) {
        setBoardFilter(savedPrefs['boardFilter']);
      }
    }
  }, [savedPrefs]);

  const [selectedDates, setSelectedDates] = useState<string[]>(() => {
    const threeDays = getLastThreeTradingDays();
    if (threeDays.length > 0) {
      return [...threeDays].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    }
    return [];
  });
  const [selectedStocks, setSelectedStocks] = useState<string[]>(() => {
    // Try to load from preferences first
    if (savedPrefs?.selectedStocks && savedPrefs.selectedStocks.length > 0) {
      return savedPrefs.selectedStocks;
    }
    // Fallback to prop or default
    if (propSelectedStock) {
      return [propSelectedStock];
    }
    return ['BBCA'];
  });

  // Real data states - changed to store data per stock
  const [priceDataByStockAndDate, setPriceDataByStockAndDate] = useState<{ [stock: string]: { [date: string]: PriceData[] } }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [_availableDates] = useState<string[]>([]);
  const [shouldFetchData, setShouldFetchData] = useState<boolean>(false);
  const [isDataReady, setIsDataReady] = useState<boolean>(false);

  // OHLC Data for coloring
  interface OhlcRow {
    time: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
  }
  const [ohlcDataByStock, setOhlcDataByStock] = useState<{ [stock: string]: OhlcRow[] }>({});

  // UI states
  const [stockInput, setStockInput] = useState('');
  const [showStockSuggestions, setShowStockSuggestions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [startDate, setStartDate] = useState(() => {
    if (!isDashboard && savedPrefs?.startDate) {
      return savedPrefs.startDate;
    }
    const threeDays = getLastThreeTradingDays();
    if (threeDays.length > 0) {
      const sortedDates = [...threeDays].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      return sortedDates[0];
    }
    return '';
  });
  const [endDate, setEndDate] = useState(() => {
    if (!isDashboard && savedPrefs?.endDate) {
      return savedPrefs.endDate;
    }
    const threeDays = getLastThreeTradingDays();
    if (threeDays.length > 0) {
      const sortedDates = [...threeDays].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      return sortedDates[sortedDates.length - 1];
    }
    return '';
  });
  // Broker selection states
  const [selectedBrokers, setSelectedBrokers] = useState<string[]>(() => {
    // Try to load from preferences first
    if (savedPrefs?.selectedBrokers && savedPrefs.selectedBrokers.length > 0) {
      return savedPrefs.selectedBrokers;
    }
    // Fallback to empty
    return [];
  });
  const [availableBrokers, setAvailableBrokers] = useState<string[]>([]);

  // Filter states - Default: F/D=All, Board=RG
  const [invFilter, setInvFilter] = useState<'F' | 'D' | ''>(() => {
    // Try to load from preferences first
    if (savedPrefs?.invFilter !== undefined) {
      return savedPrefs.invFilter;
    }
    // Fallback to default
    return '';
  }); // Default: All (empty = all)
  const [boardFilter, setBoardFilter] = useState<'RG' | 'TN' | 'NG' | ''>(() => {
    // Try to load from preferences first
    if (savedPrefs?.boardFilter) {
      return savedPrefs.boardFilter;
    }
    // Fallback to default
    return 'RG';
  }); // Default: RG
  const [showFrequency, setShowFrequency] = useState<boolean>(() => {
    // Try to load from preferences first
    if (savedPrefs?.showFrequency !== undefined) {
      return savedPrefs.showFrequency;
    }
    // Fallback to default
    return true;
  }); // Show/hide Frequency columns (BFreq, SFreq, TFreq, BLot/F, SLot/F)
  const [showOrdColumns, setShowOrdColumns] = useState<boolean>(() => {
    if (isDashboard) {
      return false;
    }
    // Try to load from preferences first
    if (savedPrefs?.showOrdColumns !== undefined) {
      return savedPrefs.showOrdColumns;
    }
    // Fallback to default
    return true;
  }); // Show/hide Ord columns

  // Initializing pending states with current active states
  const [pendingInvFilter, setPendingInvFilter] = useState<'F' | 'D' | ''>(invFilter);
  const [pendingBoardFilter, setPendingBoardFilter] = useState<'RG' | 'TN' | 'NG' | ''>(boardFilter);
  const [pendingShowFrequency, setPendingShowFrequency] = useState<boolean>(showFrequency);
  const [pendingShowOrdColumns, setPendingShowOrdColumns] = useState<boolean>(showOrdColumns);

  // Sync pending states if active states change externally (e.g., preference loading)
  useEffect(() => {
    setPendingInvFilter(invFilter);
  }, [invFilter]);
  useEffect(() => {
    setPendingBoardFilter(boardFilter);
  }, [boardFilter]);
  useEffect(() => {
    setPendingShowFrequency(showFrequency);
  }, [showFrequency]);
  useEffect(() => {
    setPendingShowOrdColumns(showOrdColumns);
  }, [showOrdColumns]);

  // Menu container ref for responsive layout
  const menuContainerRef = useRef<HTMLDivElement>(null);


  // Helper functions
  const handleStockSelect = (stock: string) => {
    if (disableTickerSelection) {
      // Ignore selection if ticker selection is disabled
      return;
    }
    // Single-ticker mode: replace selection instead of appending
    setSelectedStocks([stock]);
    setStockInput('');
    setShowStockSuggestions(false);
  };

  const handleRemoveStock = (stock: string) => {
    if (disableTickerSelection) {
      // Ignore removal if ticker selection is disabled
      return;
    }
    setSelectedStocks(selectedStocks.filter(s => s !== stock));
  };

  const handleStockInputChange = (value: string) => {
    setStockInput(value);
    setShowStockSuggestions(true);
  };

  const filteredStocks = searchStocks(stockInput);


  // Load available brokers on mount
  useEffect(() => {
    let isMounted = true;
    const loadBrokers = async () => {
      try {
        const response = await api.getBrokerList();
        if (!isMounted) return;
        if (response.success && response.data?.brokers) {
          setAvailableBrokers(response.data.brokers.sort());
        }
      } catch (error) {
        console.error('Error loading brokers:', error);
      }
    };
    loadBrokers();

    return () => {
      isMounted = false;
    };
  }, []);

  // Load stock list from backend on mount
  useEffect(() => {
    loadStockList();
  }, []);

  // Fetch available dates to set default 3 trading days accurately
  useEffect(() => {
    let isMounted = true;
    const fetchDates = async () => {
      try {
        const response = await api.getBrokerTransactionDates();
        if (!isMounted) return;
        if (response.success && response.data?.dates) {
          const convertedDates = response.data.dates.map((dateStr: string) => {
            if (dateStr.length === 8 && !dateStr.includes('-')) {
              return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
            }
            return dateStr;
          });

          const sortedDates = convertedDates.sort((a: string, b: string) => new Date(b).getTime() - new Date(a).getTime());
          const latestThree = sortedDates.slice(0, 3).reverse();

          if (isDashboard || (!savedPrefs?.startDate || !savedPrefs?.endDate)) {
            if (latestThree.length > 0) {
              setStartDate(latestThree[0]);
              setEndDate(latestThree[latestThree.length - 1]);
              setSelectedDates(latestThree);
              
              if (isDashboard && propSelectedStock && isActive) {
                // In dashboard, trigger fetch immediately once dates are loaded
                dashboardAutoFetchRef.current = propSelectedStock;
                setShouldFetchData(true);
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to load available dates:', error);
      }
    };
    fetchDates();

    return () => {
      isMounted = false;
    };
  }, [isDashboard, savedPrefs, isActive]);




  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStockSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  // No need to load stocks from API anymore - using static list

  // Update selectedStocks when prop changes
  // If disableTickerSelection is true, always force selectedStocks to match propSelectedStock
  useEffect(() => {
    if (disableTickerSelection && propSelectedStock) {
      // Force to use only propSelectedStock
      if (selectedStocks.length !== 1 || selectedStocks[0] !== propSelectedStock) {
        setSelectedStocks([propSelectedStock]);
      }
    } else if (propSelectedStock && !selectedStocks.includes(propSelectedStock)) {
      setSelectedStocks([propSelectedStock]);
    }
  }, [propSelectedStock, selectedStocks, disableTickerSelection]);

  // Auto-trigger data fetch when isDashboard is true (on mount and when propSelectedStock changes)
  const dashboardAutoFetchRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isDashboard || !propSelectedStock || !isActive) return;
    if (dashboardAutoFetchRef.current === propSelectedStock) return;
    
    const timer = setTimeout(() => {
      if (startDate && endDate) {
        dashboardAutoFetchRef.current = propSelectedStock;
        
        let datesToUse: string[] = [];
        if (isDashboard && selectedDates.length > 0) {
          // In dashboard mode, strictly use the dates fetched from the API
          datesToUse = selectedDates;
        } else {
          // Local fallback calculation for non-dashboard or if selectedDates is empty
          const start = new Date(startDate);
          const end = new Date(endDate);
          if (start <= end) {
            const dateArray: string[] = [];
            const currentDate = new Date(start);
            while (currentDate <= end) {
              const dayOfWeek = currentDate.getDay();
              if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                dateArray.push(`${year}-${month}-${day}`);
              }
              currentDate.setDate(currentDate.getDate() + 1);
            }
            datesToUse = dateArray.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
          }
        }
        
        setInvFilter(pendingInvFilter);
        setBoardFilter(pendingBoardFilter);
        setShowFrequency(pendingShowFrequency);
        setShowOrdColumns(pendingShowOrdColumns);
        setSelectedDates(datesToUse);
        setPriceDataByStockAndDate({});
        setIsDataReady(false);
        setShouldFetchData(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isDashboard, propSelectedStock, startDate, endDate]);

  // Save preferences to localStorage with debounce to reduce write operations
  useEffect(() => {
    const timeout = setTimeout(() => {
      const preferences: Partial<UserPreferences> = {
        selectedStocks,
        selectedBrokers,
        invFilter,
        boardFilter,
        showFrequency,
        showOrdColumns,
      };
      // Only include dates if they have values
      if (startDate && !isDashboard) {
        preferences.startDate = startDate;
      }
      if (endDate && !isDashboard) {
        preferences.endDate = endDate;
      }
      savePreferences(effectivePageId, preferences);
    }, 500); // Debounce 500ms to reduce localStorage writes

    return () => clearTimeout(timeout);
  }, [selectedStocks, selectedBrokers, invFilter, boardFilter, showFrequency, showOrdColumns, startDate, endDate]);

  // Fetch OHLC data when selectedStocks changes
  // Loaded directly from Azure Blob (trading-data-v2/ohlc/stocks/{sector}/{ticker}.parquet,
  // output of scheduler task 0c) via SAS token, same method as OrderbookReplay.tsx /
  // BrokerInventoryPage.tsx
  useEffect(() => {
    let isMounted = true;
    const fetchOhlcData = async () => {
      // Wait until the date range is known so this kicks off alongside the
      // zip/done-summary fetch (both become ready together via the
      // auto-load effects below) instead of starting earlier on mount.
      if (selectedDates.length === 0) return;
      const stocksToFetch = selectedStocks.filter(stock => !ohlcDataByStock[stock]);
      if (stocksToFetch.length === 0) return;

      const newOhlcData = { ...ohlcDataByStock };

      try {
        // Look up each stock's OHLC blob path client-side (cached sector
        // mapping) in parallel with the SAS token fetch, then fetch the
        // parquet directly from blob storage (cached via loadOhlcByPath) -
        // avoids the slow /api/orderbook-replay/ohlc-path lookup entirely.
        const [tokenRes, pathResults] = await Promise.all([
          api.getOrderbookReplaySasToken(),
          Promise.all(stocksToFetch.map(stock => getOhlcBlobPath(stock))),
        ]);
        if (!isMounted) return;
        if (!tokenRes.success || !tokenRes.data) {
          throw new Error(tokenRes.error || 'Failed to get blob access token');
        }
        const { containerUrl, sasToken } = tokenRes.data;

        await Promise.all(stocksToFetch.map(async (stock, i) => {
          try {
            const ohlcPath = pathResults[i];
            if (!ohlcPath) {
              console.error(`No OHLC data found for ${stock}`);
              return;
            }
            const ohlcRows = await loadOhlcByPath(ohlcPath, containerUrl, sasToken);
            if (!isMounted) return;
            const parsed: OhlcRow[] = ohlcRows.map((row: any) => {
              const dateStr = row.Date || row.date || '';
              const time = new Date(dateStr).getTime() / 1000;
              return {
                time: Math.floor(time),
                open: parseFloat(row.Open || row.open || 0),
                high: parseFloat(row.High || row.high || 0),
                low: parseFloat(row.Low || row.low || 0),
                close: parseFloat(row.Close || row.close || 0),
                volume: parseFloat(row.Volume || row.volume || 0)
              };
            }).filter((row: OhlcRow) => row.time > 0);
            newOhlcData[stock] = parsed;
          } catch (err) {
            console.error(`Failed to fetch OHLC for ${stock}`, err);
          }
        }));
      } catch (err) {
        console.error('Failed to fetch OHLC SAS token', err);
      }

      if (!isMounted) return;
      setOhlcDataByStock(newOhlcData);
    };

    fetchOhlcData();

    return () => {
      isMounted = false;
    };
  }, [selectedStocks, selectedDates]);

  // Auto-load data from saved preferences on mount (after initial data is loaded)
  useEffect(() => {
    // We rely on the API to set default dates now via the fetchDates hook
    // Only proceed if we have valid saved prefs and we're not in dashboard
    if (isDashboard || !savedPrefs?.startDate || !savedPrefs?.endDate) {
      return; 
    }

    const savedStartDate = savedPrefs.startDate;
    const savedEndDate = savedPrefs.endDate;

    // Wait a bit to ensure initial data (stocks, brokers) are loaded
    const timer = setTimeout(() => {
      // Generate date array from saved startDate and endDate (only trading days)
      const startParts = savedStartDate.split('-').map(Number);
      const endParts = savedEndDate.split('-').map(Number);

      if (startParts.length === 3 && endParts.length === 3) {
        const startYear = startParts[0];
        const startMonth = startParts[1];
        const startDay = startParts[2];
        const endYear = endParts[0];
        const endMonth = endParts[1];
        const endDay = endParts[2];

        if (startYear !== undefined && startMonth !== undefined && startDay !== undefined &&
          endYear !== undefined && endMonth !== undefined && endDay !== undefined) {
          const start = new Date(startYear, startMonth - 1, startDay);
          const end = new Date(endYear, endMonth - 1, endDay);

          // Check if range is valid
          if (start <= end) {
            // Generate date array (only trading days)
            const dateArray: string[] = [];
            const currentDate = new Date(start);

            while (currentDate <= end) {
              const dayOfWeek = currentDate.getDay();
              // Skip weekends (Saturday = 6, Sunday = 0)
              if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                // Format as YYYY-MM-DD in local timezone
                const year = currentDate.getFullYear();
                const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                const day = String(currentDate.getDate()).padStart(2, '0');
                const dateString = `${year}-${month}-${day}`;
                dateArray.push(dateString);
              }
              // Move to next day
              currentDate.setDate(currentDate.getDate() + 1);
            }

            // Sort by date (oldest first) for display
            const datesToUse = dateArray.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

            // Update selectedDates
            setSelectedDates(datesToUse);

            // Trigger auto-load by setting shouldFetchData to true
            setShouldFetchData(true);
          }
        }
      }
    }, 500); // Small delay to ensure initial data is loaded

    return () => clearTimeout(timer);
  }, []); // Only run once on mount

  // Fetch data when shouldFetchData is true
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      if (!shouldFetchData) {
        return;
      }

      if (selectedStocks.length === 0 || selectedDates.length === 0) {
        console.log('Skipping fetch - missing stocks or dates:', { selectedStocks, selectedDates });
        setShouldFetchData(false);
        return;
      }

      console.log('Starting to fetch data for:', { selectedStocks, selectedDates, selectedBrokers, invFilter, boardFilter });
      setLoading(true);
      setError(null);
      setIsDataReady(false);

      try {
        // Determine fd parameter: if empty, use 'all', otherwise use lowercase
        const fdParam = invFilter === '' ? 'all' : invFilter.toLowerCase();

        // Determine boards to fetch: when "All" is selected in UI (boardFilter === ''),
        // we STRICTLY fetch 'rg', 'tn', and 'ng' and sum them in the frontend.
        // We avoid the backend 'all' board parameter as requested.
        const boardsToFetch = boardFilter === '' ? ['rg', 'tn', 'ng'] : [boardFilter.toLowerCase()];

        // Determine brokers to fetch: if empty, use 'All', otherwise fetch all selected brokers
        const brokersToFetch = selectedBrokers.length === 0 ? ['All'] : selectedBrokers;

        console.log('📊 FetchData Parameters:', { brokersToFetch, fdParam, boardsToFetch, boardFilter });

        // Fetch done_summary CSVs directly from Azure Blob (trading-data-v2,
        // done_summary_broker_breakdown / done_summary_haka_haki) via SAS token,
        // same method as BrokerSummaryPage.tsx / brokerSummaryBlob.ts
        const tokenRes = await api.getOrderbookReplaySasToken();
        if (!isMounted) return;
        if (!tokenRes.success || !tokenRes.data) {
          throw new Error(tokenRes.error || 'Failed to get blob access token');
        }
        const { containerUrl, sasToken } = tokenRes.data;

        const allRequests: Array<{ stock: string; date: string; broker: string; board: string; promise: Promise<Record<string, unknown>[]> }> = [];

        selectedStocks.forEach(stock => {
          selectedDates.forEach(date => {
            brokersToFetch.forEach(broker => {
              boardsToFetch.forEach(board => {
                const paths = getDoneSummaryBlobPath(stock, date, broker, fdParam, board);
                allRequests.push({
                  stock,
                  date,
                  broker,
                  board,
                  promise: fetchDoneSummaryCsv(paths, containerUrl, sasToken).then(csv => csv ? parseDoneSummaryCSV(csv) : [])
                });
              });
            });
          });
        });

        const allResults = await Promise.allSettled(allRequests.map(p => p.promise));

        if (!isMounted) return;

        // Store raw data per stock, date and broker (aggregated across boards)
        // Structure: stock -> date -> broker -> board -> records
        const dataMapByBoard: { [stock: string]: { [date: string]: { [broker: string]: { [board: string]: any[] } } } } = {};

        allResults.forEach((result, index) => {
          const requestData = allRequests[index];
          if (!requestData) return;

          const { stock, date, broker, board } = requestData;
          if (!stock || !date) return;

          if (!dataMapByBoard[stock]) dataMapByBoard[stock] = {};
          if (!dataMapByBoard[stock][date]) dataMapByBoard[stock][date] = {};
          if (!dataMapByBoard[stock][date][broker]) dataMapByBoard[stock][date][broker] = {};

          if (result.status === 'fulfilled') {
            const records = result.value;
            console.log(`✅ Received ${records.length} records for ${stock} on ${date} (Broker: ${broker}, Board: ${board})`);
            dataMapByBoard[stock][date][broker][board] = records;
          } else {
            console.error(`❌ Blob fetch error for ${stock}/${date}/${broker}/${board}:`, result.reason);
            dataMapByBoard[stock][date][broker][board] = [];
          }
        });

        // Store aggregated raw data
        const rawDataByStockDateAndBroker: { [stock: string]: { [date: string]: { [broker: string]: any[] } } } = {};

        Object.entries(dataMapByBoard).forEach(([stock, dateMap]) => {
          if (!rawDataByStockDateAndBroker[stock]) {
            rawDataByStockDateAndBroker[stock] = {};
          }
          const stockObj = rawDataByStockDateAndBroker[stock];

          Object.entries(dateMap).forEach(([date, brokerMap]) => {
            if (!stockObj[date]) {
              stockObj[date] = {};
            }
            const dateObj = stockObj[date];

            Object.entries(brokerMap).forEach(([broker, boardMap]) => {
              // Strategy for aggregating boards:
              // Strictly sum rg, tn, and ng when boardFilter is empty (All Board).

              if (boardFilter === '') {
                const rgRecords = boardMap['rg'] || [];
                const tnRecords = boardMap['tn'] || [];
                const ngRecords = boardMap['ng'] || [];

                console.log(`📦 Aggregating ${stock}/${date}/${broker}: RG(${rgRecords.length}) + TN(${tnRecords.length}) + NG(${ngRecords.length})`);

                // Concatenate all records. The subsequent price-level aggregation will sum them.
                dateObj[broker] = [...rgRecords, ...tnRecords, ...ngRecords];
              } else {
                // When a specific board is selected
                const selected = boardFilter.toLowerCase();
                dateObj[broker] = boardMap[selected] || [];
              }
            });
          });
        });

        // Aggregate data from all brokers per stock and date
        const newPriceDataByStockAndDate: { [stock: string]: { [date: string]: PriceData[] } } = {};

        selectedStocks.forEach(stock => {
          newPriceDataByStockAndDate[stock] = {};

          selectedDates.forEach(date => {
            const brokerDataMap = rawDataByStockDateAndBroker[stock]?.[date] || {};

            // Aggregate data from all brokers by price level
            const aggregatedByPrice = new Map<number, {
              bFreq: number;
              bLot: number;
              bOrd: number;
              sLot: number;
              sFreq: number;
              sOrd: number;
              tFreq: number;
              tLot: number;
              tOrd: number;
            }>();

            // Collect all prices first to preserve order
            const allPrices = new Set<number>();

            // Process data from each broker
            Object.entries(brokerDataMap).forEach(([_broker, records]) => {
              if (!Array.isArray(records)) return;

              records.forEach((row: any) => {
                // Robust price extraction: handle row.Price, row.price, and potential string conversion
                const rawPrice = row.Price ?? row.price;
                const price = typeof rawPrice === 'number' ? rawPrice : parseFloat(String(rawPrice || '0').replace(/,/g, ''));
                if (!price || isNaN(price)) return;
                allPrices.add(price);

                if (!aggregatedByPrice.has(price)) {
                  aggregatedByPrice.set(price, {
                    bFreq: 0,
                    bLot: 0,
                    bOrd: 0,
                    sLot: 0,
                    sFreq: 0,
                    sOrd: 0,
                    tFreq: 0,
                    tLot: 0,
                    tOrd: 0
                  });
                }

                const priceData = aggregatedByPrice.get(price)!;

                // Robust normalization for all possible field name variations (Standard and HAKA/HAKI)
                let bFreq = Number(row.Bfreq || row.BFreq || row.bFreq || row.BFREQ) || 0;
                let sFreq = Number(row.Sfreq || row.SFreq || row.sFreq || row.SFREQ) || 0;
                let tFreq = Number(row.Tfreq || row.TFreq || row.tFreq || row.TFREQ) || 0;

                let bOrd = Number(row.Bor || row.BOrd || row.bOrd || row.BORD || row.BOrder) || 0;
                let sOrd = Number(row.Sor || row.SOrd || row.sOrd || row.SORD || row.SOrder) || 0;
                let tOrd = Number(row.Tor || row.TOr || row.tOrd || row.TORD || row.TOrder) || 0;

                // Volume/Lot handling: HAKA/HAKI is usually in shares (div 100), Standard is in Lots
                const isHakaHaki = row.HAKI !== undefined || row.HAKA !== undefined || row.Haki !== undefined || row.Haka !== undefined || row.haki !== undefined || row.haka !== undefined;

                let bLot = 0;
                let sLot = 0;
                let tLot = 0;

                if (isHakaHaki) {
                  bLot = (Number(row.HAKI || row.Haki || row.haki) || 0) / 100;
                  sLot = (Number(row.HAKA || row.Haka || row.haka) || 0) / 100;
                  tLot = (Number(row.TLot || row.Tlot || row.tlot || row.TLOT) || 0) / 100;
                } else {
                  // broker_breakdown CSV values are STK_VOLM (shares), same as
                  // haka_haki — divide by 100 to get lots.
                  bLot = (Number(row.BLot || row.Blot || row.bLot || row.BLOT) || 0) / 100;
                  sLot = (Number(row.SLot || row.Slot || row.sLot || row.SLOT) || 0) / 100;
                  tLot = (Number(row.TLot || row.Tlot || row.tlot || row.TLOT) || 0) / 100;
                }

                // If tLot is provided but B/S sides are 0 (e.g. some Negotiated records), 
                // we'll attribute to both sides or at least ensure totals match.
                // Assuming cross if B/S are zero but T is present.
                if (tLot > 0 && bLot === 0 && sLot === 0) {
                  bLot = tLot; // Treat as cross for NG
                  sLot = tLot;
                  if (bFreq === 0 && sFreq === 0 && tFreq > 0) { bFreq = tFreq; sFreq = tFreq; }
                  if (bOrd === 0 && sOrd === 0 && tOrd > 0) { bOrd = tOrd; sOrd = tOrd; }
                }

                priceData.bFreq += bFreq;
                priceData.bLot += bLot;
                priceData.bOrd += bOrd;
                priceData.sFreq += sFreq;
                priceData.sLot += sLot;
                priceData.sOrd += sOrd;
              });
            });

            // Convert aggregated data to PriceData format
            // Sort prices in descending order (highest first)
            const sortedPrices = Array.from(allPrices).sort((a, b) => b - a);

            const priceDataArray: PriceData[] = sortedPrices.map(price => {
              const agg = aggregatedByPrice.get(price)!;

              return {
                price,
                bFreq: agg.bFreq,
                bLot: agg.bLot,
                bOrd: agg.bOrd,
                sLot: agg.sLot,
                sFreq: agg.sFreq,
                sOrd: agg.sOrd,
                tFreq: agg.bFreq + agg.sFreq,
                tLot: agg.bLot + agg.sLot,
                tOrd: agg.bOrd + agg.sOrd
              };
            });

            if (!newPriceDataByStockAndDate[stock]) {
              newPriceDataByStockAndDate[stock] = {};
            }
            newPriceDataByStockAndDate[stock][date] = priceDataArray;
          });
        });

        setPriceDataByStockAndDate(newPriceDataByStockAndDate);
        setIsDataReady(true);

      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
        setIsDataReady(false);
        showToast({
          type: 'error',
          title: 'Error',
          message: 'Failed to load data. Please try again.'
        });
      } finally {
        setLoading(false);
        setShouldFetchData(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [shouldFetchData, selectedStocks, selectedDates, selectedBrokers, invFilter, boardFilter, showToast]);


  const globalVisibleDates = useMemo(() => {
    const datesWithData = new Set<string>();
    selectedStocks.forEach(stock => {
      const stockData = priceDataByStockAndDate[stock] || {};
      selectedDates.forEach(date => {
        if (stockData[date] && stockData[date].length > 0) {
          datesWithData.add(date);
        }
      });
    });
    // Return selectedDates filtered by the union set, to preserve order
    return selectedDates.filter(date => datesWithData.has(date));
  }, [selectedStocks, selectedDates, priceDataByStockAndDate]);

  // Memoized data processing for all stocks to optimize rendering
  const processedStockData = useMemo(() => {
    if (!isDataReady) return new Map();

    const stockMap = new Map();

    selectedStocks.forEach(stock => {
      const stockPriceDataByDate = priceDataByStockAndDate[stock] || {};
      const visibleDates = globalVisibleDates;

      // Convert Array to Map for O(1) lookup: date -> price -> data
      const dataByDateMap = new Map<string, Map<number, PriceData>>();
      visibleDates.forEach(date => {
        const dateMap = new Map<number, PriceData>();
        const dateData = stockPriceDataByDate[date] || [];
        dateData.forEach(item => dateMap.set(item.price, item));
        dataByDateMap.set(date, dateMap);
      });

      // All unique prices (pre-calculated to avoid repeat logic during render)
      const allPrices = getAllUniquePrices(stock, visibleDates, stockPriceDataByDate, ohlcDataByStock[stock]);

      // Pre-calculate row totals (Grand Total column)
      const rowTotals = new Map<number, PriceData>();
      allPrices.forEach(price => {
        let bFreq = 0, sFreq = 0, bLot = 0, sLot = 0, bOrd = 0, sOrd = 0;
        visibleDates.forEach(date => {
          const data = dataByDateMap.get(date)?.get(price);
          if (data) {
            bFreq += data.bFreq || 0;
            sFreq += data.sFreq || 0;
            bLot += data.bLot || 0;
            sLot += data.sLot || 0;
            bOrd += data.bOrd || 0;
            sOrd += data.sOrd || 0;
          }
        });
        rowTotals.set(price, {
          price,
          bFreq, sFreq, bLot, sLot, bOrd, sOrd,
          tFreq: bFreq + sFreq,
          tLot: bLot + sLot,
          tOrd: bOrd + sOrd
        });
      });

      // Pre-calculate date totals (Footer row)
      const dateTotals = new Map<string, any>();
      visibleDates.forEach(date => {
        const dateData = stockPriceDataByDate[date] || [];
        dateTotals.set(date, calculateTotals(dateData));
      });

      // Pre-calculate grand grand totals
      const grandTotals = Array.from(dateTotals.values()).reduce((acc, curr) => ({
        bFreq: acc.bFreq + curr.bFreq,
        bLot: acc.bLot + curr.bLot,
        bOrd: acc.bOrd + curr.bOrd,
        sLot: acc.sLot + curr.sLot,
        sFreq: acc.sFreq + curr.sFreq,
        sOrd: acc.sOrd + curr.sOrd,
        tFreq: 0,
        tLot: 0,
        tOrd: 0
      }), { bFreq: 0, bLot: 0, bOrd: 0, sLot: 0, sFreq: 0, sOrd: 0, tFreq: 0, tLot: 0, tOrd: 0 });

      grandTotals.tFreq = grandTotals.bFreq + grandTotals.sFreq;
      grandTotals.tLot = grandTotals.bLot + grandTotals.sLot;
      grandTotals.tOrd = grandTotals.bOrd + grandTotals.sOrd;

      stockMap.set(stock, {
        allPrices,
        dataByDateMap,
        rowTotals,
        dateTotals,
        grandTotals
      });
    });

    return stockMap;
  }, [isDataReady, selectedStocks, globalVisibleDates, priceDataByStockAndDate, ohlcDataByStock]);



  const formatDateForInput = (date: string | undefined) => {
    return date || '';
  };

  const triggerDatePicker = (inputRef: React.RefObject<HTMLInputElement>) => {
    inputRef.current?.showPicker?.();
  };

  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  return (
    <div className="w-full">
      {/* Top Controls - Compact without Card */}
      {/* On small/medium screens menu scrolls bersama konten. Hanya di layar besar (lg+) yang fixed di top. */}
      <div className="bg-card border-b border-[#3a4252] px-4 py-1.5 lg:sticky lg:top-0 lg:z-40">
        <div ref={menuContainerRef} className="flex flex-col md:flex-row md:flex-wrap items-center gap-2 md:gap-x-7 md:gap-y-0.2">
          {/* Ticker Selection (hidden in dashboard mode) */}
          {!isDashboard && (
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Ticker:</label>
            <div className="relative flex-1 md:flex-none" ref={dropdownRef}>
              {disableTickerSelection ? (
                <div className="w-full md:w-32 h-9 pl-3 pr-3 flex items-center text-sm border border-input rounded-md bg-muted text-foreground">
                  {propSelectedStock || selectedStocks[0] || 'No ticker selected'}
                </div>
              ) : (
                <>
                  <div className="w-full md:w-32 h-9 px-3 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground hover:border-primary/50 focus-within:border-primary/50 transition-colors">
                    <Search className="w-3.5 h-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                    <input
                      type="text"
                      className="w-full bg-transparent text-sm text-foreground focus:outline-none placeholder:text-muted-foreground/50 truncate"
                      placeholder="Select ticker..."
                      value={showStockSuggestions ? stockInput : (selectedStocks.length > 0 ? selectedStocks.join(', ') : '')}
                      onChange={(e) => {
                        handleStockInputChange(e.target.value);
                        setShowStockSuggestions(true);
                      }}
                      onFocus={() => setShowStockSuggestions(true)}
                    />
                    <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0 cursor-pointer" onClick={() => setShowStockSuggestions(!showStockSuggestions)} />
                  </div>
                  {showStockSuggestions && (
                    <div id="stock-suggestions" role="listbox" className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 w-full overflow-hidden flex flex-col">
                      {selectedStocks.length > 0 && (
                        <div className="px-3 py-2 text-xs text-muted-foreground border-b border-[#3a4252] flex items-center justify-between bg-popover">
                          <span>Selected ({selectedStocks.length})</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedStocks([]);
                            }}
                            className="text-xs text-destructive hover:text-destructive/80 font-medium"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                      {/* Search Input (Removed) */}
                      <div className="max-h-60 overflow-y-auto py-1">
                        {(() => {
                          const filteredList = stockInput === ''
                            ? STOCK_LIST.slice(0, 30)
                            : filteredStocks.slice(0, 30);
                          return filteredList.map(stock => {
                            const isSelected = selectedStocks.includes(stock);
                            return (
                              <div
                                key={stock}
                                className={`px-3 py-[2.06px] text-sm cursor-pointer hover:bg-accent flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : 'text-foreground'}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSelected) {
                                    handleRemoveStock(stock);
                                  } else {
                                    handleStockSelect(stock);
                                  }
                                }}
                              >
                                <span>{stock}</span>
                                {isSelected && <span className="text-primary text-xs">✓</span>}
                              </div>
                            );
                          });
                        })()}
                        {stockInput !== '' && filteredStocks.length === 0 && (
                          <div className="px-3 py-2 text-sm text-muted-foreground text-center">No stocks found</div>
                        )}
                        {stockInput === '' && STOCK_LIST.length > 30 && (
                          <div className="px-3 py-2 text-xs text-muted-foreground text-center border-t border-[#3a4252]">
                            Type to search {STOCK_LIST.length - 30} more stocks
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          )}

          {/* Broker Selection - Multi-select with dropdown only (disabled for trial users) */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Broker:</label>
            <BrokerSelector
              selectedBrokers={selectedBrokers}
              setSelectedBrokers={setSelectedBrokers}
              availableBrokers={availableBrokers}
              isTrial={isTrial}
              showToast={showToast}
            />
          </div>



          {/* Date Range */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Date Range:</label>
            <div className="flex items-center gap-2 w-full md:w-auto">
              <div
                className="relative h-9 flex-1 md:w-36 rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => triggerDatePicker(startDateRef)}
              >
                <input
                  ref={startDateRef}
                  type="date"
                  value={formatDateForInput(startDate)}
                  onChange={(e) => {
                    const selectedDate = new Date(e.target.value);
                    const dayOfWeek = selectedDate.getDay();
                    if (dayOfWeek === 0 || dayOfWeek === 6) {
                      showToast({
                        type: 'warning',
                        title: 'Peringatan',
                        message: 'Tidak bisa memilih hari Sabtu atau Minggu'
                      });
                      return;
                    }
                    setStartDate(e.target.value);
                    if (!endDate || new Date(e.target.value) > new Date(endDate)) {
                      setEndDate(e.target.value);
                    }
                  }}
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onInput={(e) => e.preventDefault()}
                  max={formatDateForInput(endDate)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  style={{ caretColor: 'transparent' }}
                />
                <div className="flex items-center justify-between h-full px-3">
                  <span className="text-sm text-foreground">
                    {startDate ? new Date(startDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }) : 'DD/MM/YYYY'}
                  </span>
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </div>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap hidden md:inline">to</span>
              <div
                className="relative h-9 flex-1 md:w-36 rounded-md border border-input bg-background cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => triggerDatePicker(endDateRef)}
              >
                <input
                  ref={endDateRef}
                  type="date"
                  value={formatDateForInput(endDate)}
                  onChange={(e) => {
                    const selectedDate = new Date(e.target.value);
                    const dayOfWeek = selectedDate.getDay();
                    if (dayOfWeek === 0 || dayOfWeek === 6) {
                      showToast({
                        type: 'warning',
                        title: 'Peringatan',
                        message: 'Tidak bisa memilih hari Sabtu atau Minggu'
                      });
                      return;
                    }
                    const newEndDate = e.target.value;
                    setEndDate(newEndDate);
                    if (startDate && new Date(newEndDate) < new Date(startDate)) {
                      setStartDate(newEndDate);
                    }
                  }}
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  onInput={(e) => e.preventDefault()}
                  min={formatDateForInput(startDate)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  style={{ caretColor: 'transparent' }}
                />
                <div className="flex items-center justify-between h-full px-3">
                  <span className="text-sm text-foreground">
                    {endDate ? new Date(endDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }) : 'DD/MM/YYYY'}
                  </span>
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </div>
              </div>
            </div>
          </div>

          {/* F/D Filter (Foreign/Domestic) */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">F/D:</label>
            <select
              value={pendingInvFilter}
              onChange={(e) => {
                setPendingInvFilter(e.target.value as 'F' | 'D' | '');
              }}
              className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
            >
              <option value="">All</option>
              <option value="F">Foreign</option>
              <option value="D">Domestic</option>
            </select>
          </div>

          {/* Board Filter (RG/TN/NG) */}
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Board:</label>
            <select
              value={pendingBoardFilter}
              onChange={(e) => {
                setPendingBoardFilter(e.target.value as 'RG' | 'TN' | 'NG' | '');
              }}
              className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
            >
              <option value="">All</option>
              <option value="RG">RG</option>
              <option value="TN">TN</option>
              <option value="NG">NG</option>
            </select>
          </div>

          {/* Frequency and Ord Toggles */}
          <div className="flex flex-col gap-1 items-start">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={pendingShowFrequency}
                onChange={(e) => setPendingShowFrequency(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-[#3a4252] text-primary focus:ring-primary cursor-pointer"
              />
              <span className="text-xs text-foreground whitespace-nowrap">Freq</span>
            </label>
            <label 
              className={`flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity`}
              onClick={(e) => {
                if (isTrial) {
                  e.preventDefault();
                  showToast({
                    type: 'warning',
                    title: 'Fitur Terbatas',
                    message: 'Dalam masa free trial, fitur pilihan Order tidak tersedia.'
                  });
                }
              }}
            >
              <input
                type="checkbox"
                checked={isTrial ? false : pendingShowOrdColumns}
                onChange={(e) => {
                  if (isTrial) {
                    e.preventDefault();
                    return;
                  }
                  setPendingShowOrdColumns(e.target.checked);
                }}
                className="w-3.5 h-3.5 rounded border-[#3a4252] text-primary focus:ring-primary cursor-pointer"
              />
              <span className="text-xs text-foreground whitespace-nowrap">Ord</span>
            </label>
          </div>


          {/* Show Button */}
          <button
            onClick={() => {
              // Generate date array from startDate and endDate
              let datesToUse: string[] = [];
              if (startDate && endDate) {
                const start = new Date(startDate);
                const end = new Date(endDate);

                if (start <= end) {
                  const dateArray: string[] = [];
                  const currentDate = new Date(start);

                  while (currentDate <= end) {
                    const dayOfWeek = currentDate.getDay();
                    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                      const year = currentDate.getFullYear();
                      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
                      const day = String(currentDate.getDate()).padStart(2, '0');
                      const dateString = `${year}-${month}-${day}`;
                      dateArray.push(dateString);
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                  }

                  datesToUse = dateArray.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

                  // Check if total trading dates exceed 20
                  if (datesToUse.length > 20) {
                    showToast({
                      type: 'warning',
                      title: 'Terlalu Banyak Tanggal',
                      message: 'Maksimal 20 hari trading yang bisa dipilih (tidak termasuk weekend)',
                    });
                    return;
                  }
                }
              }

              // We check if stocks OR brokers changed in the current state vs what was loaded
              // By checking if current UI state is fully updated over previous state.
              // Note: If we had a previous query state, we'd compare it here. For now, since selectedBrokers/Stocks
              // directly updates the state, we can force a refresh if the user clicked show. We can clear `isDataReady` whenever they change,
              // or handle it implicitly by tracking `fetch` parameters in a reference or separate state.
              // But a simple workaround: If they clicked "Show", we always assume they want a refresh if it's new
              // Let's rely on standard state triggers or just force reload it directly!

              // Commit pending states to actual active states
              setInvFilter(pendingInvFilter);
              setBoardFilter(pendingBoardFilter);
              setShowFrequency(pendingShowFrequency);
              setShowOrdColumns(pendingShowOrdColumns);

              setSelectedDates(datesToUse);

              console.log('🔄 Reloading data due to Show click...');
              // Clear existing data before fetching new data
              setPriceDataByStockAndDate({});
              setIsDataReady(false);

              // Trigger fetch
              setShouldFetchData(true);
            }}
            disabled={loading || selectedStocks.length === 0 || !startDate || !endDate}
            className="h-[34px] px-4 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium whitespace-nowrap flex items-center justify-center w-full md:w-auto"
          >
            Show
          </button>
        </div>
      </div>



      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-16 pt-4">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Loading bid/ask data...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="flex items-center justify-center py-8 pt-4">
          <div className="text-sm text-destructive">{error}</div>
        </div>
      )}

      {/* Main Data Display */}
      <div className="pt-2 space-y-6">
        {!loading && !error && isDataReady && selectedStocks.map(stock => {
          const processedData = processedStockData.get(stock);
          if (!processedData) return null;
          return (
            <StockSummaryTable
              key={stock}
              stock={stock}
              visibleDates={globalVisibleDates}
              processedData={processedData}
              ohlcData={ohlcDataByStock[stock] || []}
              showOrdColumns={showOrdColumns}
              showFrequency={showFrequency}
              selectedBrokers={selectedBrokers}
              selectedStocksCount={selectedStocks.length}
            />
          );
        })}
      </div>
    </div>
  );
}
